import { useEffect, useState } from 'react';

/**
 * "Is this a phone?" — asked of the VIEWPORT, not of the user agent.
 *
 * Deliberately the same shape of question as isTv(): not what device this is,
 * but what the screen can hold. A 390px browser window on a desktop gets the
 * phone layout and should, because the problem is the width and nothing else.
 *
 * 720px rather than a rounder number: phones are 390-430 portrait and 700-930
 * landscape, so this covers portrait and leaves landscape on the roomier
 * layout, which is the right way round — a phone on its side has the width
 * for labels and not the height for anything else.
 *
 * MOST of the mobile work is CSS, and should be. This exists only for the two
 * decisions CSS cannot make on its own: which component renders the search
 * field, and whether the on-screen keyboard belongs on the screen.
 */
const PHONE = '(max-width: 720px)';

function matches(query: string): boolean {
  try {
    return !!window.matchMedia && window.matchMedia(query).matches;
  } catch {
    // A WebView old enough to refuse matchMedia is a television, and a
    // television is not a phone.
    return false;
  }
}

export function usePhone(): boolean {
  const [phone, setPhone] = useState(() => matches(PHONE));

  useEffect(() => {
    if (!window.matchMedia) return;
    const mq = window.matchMedia(PHONE);
    const onChange = () => setPhone(mq.matches);
    // Re-read on mount: the first render used the value at module scope, and
    // a rotation between the two is cheap to get wrong and confusing to see.
    onChange();

    /*
     * `addEventListener` on a MediaQueryList arrived in Chrome 39 but the
     * Fire TV sticks run WebViews old enough that `addListener` is still the
     * only one present, and calling the missing one throws rather than
     * failing quietly — which would take the whole shell down.
     */
    if (mq.addEventListener) {
      mq.addEventListener('change', onChange);
      return () => mq.removeEventListener('change', onChange);
    }
    mq.addListener(onChange);
    return () => mq.removeListener(onChange);
  }, []);

  return phone;
}
